import { useState, useCallback } from 'react';
import { priceOptimizationApi } from '../api/priceOptimization';
import type { PriceOptimizationInsight, GeneratePriceOptimizationPayload } from '../api/priceOptimization';

export function usePriceOptimization() {
  const [insights, setInsights] = useState<PriceOptimizationInsight[]>([]);
  const [activeInsight, setActiveInsight] = useState<PriceOptimizationInsight | null>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  const fetchInsights = useCallback(async (p = 1) => {
    setLoading(true);
    setError(null);
    try {
      const res = await priceOptimizationApi.list(p);
      setInsights(res.data.data);
      setTotal(res.data.meta.total);
      setLastPage(res.data.meta.last_page);
      setPage(p);
    } catch {
      setError('Failed to load price optimization insights.');
    } finally {
      setLoading(false);
    }
  }, []);

  const generate = useCallback(async (payload: GeneratePriceOptimizationPayload = {}) => {
    setGenerating(true);
    setError(null);
    try {
      const res = await priceOptimizationApi.generate(payload);
      const insight = res.data.data;
      setActiveInsight(insight);
      setInsights((prev) => [insight, ...prev]);
      return insight;
    } catch (err: any) {
      const msg = err?.message ?? 'Price optimization analysis failed.';
      setError(msg);
      return null;
    } finally {
      setGenerating(false);
    }
  }, []);

  const deleteInsight = useCallback(async (id: number) => {
    try {
      await priceOptimizationApi.destroy(id);
      setInsights((prev) => prev.filter((i) => i.id !== id));
      if (activeInsight?.id === id) setActiveInsight(null);
    } catch {
      setError('Failed to delete insight.');
    }
  }, [activeInsight]);

  return {
    insights, activeInsight, loading, generating, error, total, page, lastPage,
    fetchInsights, generate, deleteInsight, setActiveInsight, setError,
  };
}
